/**
 * Client-side paging helpers for the public web grids.
 *
 * The teacher list and a teacher's course list both arrive whole in a single
 * response - neither exposes page metadata - so paging is a slice of an
 * in-memory array and the page count is derived here rather than fetched.
 */

export const ELLIPSIS = "ellipsis" as const;

export type PageItem = number | typeof ELLIPSIS;

/** Page buttons to render before the rail collapses behind ellipses. */
export const MAX_NUMBERED = 5;

export function getTotalPages(itemCount: number, perPage: number): number {
  if (perPage <= 0) {
    return 1;
  }

  return Math.max(1, Math.ceil(itemCount / perPage));
}

/**
 * Keeps a page number inside the range a list can actually serve. A search or
 * a refetch can shrink the list underneath the page the user is on, which
 * would otherwise render an empty grid with no way back.
 */
export function clampPage(page: number, totalPages: number): number {
  if (!Number.isFinite(page)) {
    return 1;
  }

  return Math.min(Math.max(1, Math.trunc(page)), Math.max(1, totalPages));
}

export function slicePage<T>(items: T[], page: number, perPage: number): T[] {
  const safePage = clampPage(page, getTotalPages(items.length, perPage));

  return items.slice((safePage - 1) * perPage, safePage * perPage);
}

/**
 * Page numbers to render, with `ELLIPSIS` standing in for the skipped runs.
 * The first and last page are always present so both ends stay one click away;
 * the window slides around the current page and keeps a constant width, so the
 * rail does not visibly shrink when the user reaches either end.
 */
export function getPageItems(
  currentPage: number,
  totalPages: number,
): PageItem[] {
  if (totalPages <= MAX_NUMBERED + 2) {
    return Array.from({ length: Math.max(0, totalPages) }, (_, i) => i + 1);
  }

  const half = Math.floor(MAX_NUMBERED / 2);
  let start = Math.max(2, currentPage - half);
  let end = Math.min(totalPages - 1, currentPage + half);

  if (currentPage - half < 2) {
    end = Math.min(totalPages - 1, MAX_NUMBERED);
  }

  if (currentPage + half > totalPages - 1) {
    // `+ 1` keeps this mirror-symmetric with the near-the-start branch above,
    // which renders pages 2..MAX_NUMBERED - without it the last page shows one
    // more number than the first and the rail changes width at the end.
    start = Math.max(2, totalPages - MAX_NUMBERED + 1);
  }

  const items: PageItem[] = [1];

  if (start > 2) {
    items.push(ELLIPSIS);
  }

  for (let page = start; page <= end; page += 1) {
    items.push(page);
  }

  if (end < totalPages - 1) {
    items.push(ELLIPSIS);
  }

  items.push(totalPages);

  return items;
}
